import {Directive, HostListener, Input, OnChanges, SimpleChanges} from '@angular/core';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';

@Directive({
  selector: 'fa-icon[appShowFaSpinner]',
  exportAs: 'appShowFaSpinner',
  standalone: false
})
export class ShowFaSpinnerDirective implements OnChanges {

  private readonly SPINNER_ICON_NAME: IconProp = 'spinner';

  private readonly logger: AceLogger;

  /** When using appShowFaSpinner it is possible to optionally pass in a boolean variable to control the spinning:
   * 1. <fa-icon [appShowFaSpinner]></fa-icon>              <!-- spinning will be controlled by click -->
   *    Here appShowFaSpinner property will be left undefined
   * 2. <fa-icon appShowFaSpinner></fa-icon>                <!-- spinning will be controlled by click -->
   *    Here appShowFaSpinner property will be set to '' because it'll be treated as an html attribute,
   *    equivalent to <fa-icon appShowFaSpinner=""></fa-icon>
   * 3. <fa-icon [appShowFaSpinner]="isLoading"></fa-icon>  <!-- spinning will be controlled by 'isLoading' flag -->
   *    Here appShowFaSpinner property will be set to boolean value of isLoading
   */
  @Input()
  appShowFaSpinner: boolean | '' | undefined;

  // By defining icon as Input, we are able to read the original icon of fa-icon(host)
  @Input()
  icon: IconProp;

  constructor(private component: FaIconComponent, loggerService: AceLoggerService) {
    this.logger = loggerService.create(ShowFaSpinnerDirective.name);
  }

  @HostListener('click')
  private triggerLoading() {
    // Only respond to a click when no variable is passed in, otherwise the spinner is controlled by the variable, not a click
    if (this.respondToClick(this.appShowFaSpinner)) {
      this.toggleSpinner(true);
    }
  }

  private toggleSpinner(spin: boolean): void {
    if (this.component) {
      // @ts-ignore - God knows why the ternary operator confused TypeScript here
      this.component.icon = spin ? this.SPINNER_ICON_NAME : this.icon;
      this.component.animation = spin ? 'spin' : null;
      this.component.render();
    }
  }

  /**
   * Test whether the spinner should be controlled by click
   */
  private respondToClick(value: typeof this.appShowFaSpinner): boolean {
    return value == null || value === '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['appShowFaSpinner'];
    // Do not act on a blank value, as then the spinner should be controlled by a click
    if (change != null && !this.respondToClick(change.currentValue)) {
      this.toggleSpinner(!!change.currentValue);
    }
  }
}
